import React, { useState } from "react";
import { AnswersDisputeGetResponse } from "../../../../../api/src/users/answers/route";
import { useUpsertAnswerMutation } from "../../../api/answers";

import { SecondaryButton } from "../../../components/buttons/SecondaryButton";
import { PrimaryButton } from "../../../components/buttons/PrimaryButton";
import { Card } from "../../../components/layout/Card";
import { ParagraphHeaderLarge } from "../../../components/typography/Paragraph";

import { ReadOnlyAnswerDisplay } from "./ReadOnlyAnswerDisplay";
import { AcceptDisputeModal } from "./AcceptDisputeModal";
import { ChangeAnswerModal } from "./ChangeAnswerModal";
import { CardHeader } from "./CardHeader";
import {
  QuestionSection,
  AnswersContainer,
  CardFooter,
} from "./DisputeComponents";

interface DisputeCardProps {
  dispute: AnswersDisputeGetResponse[number];
  currentUserId: string;
  onAgreeWithPartner: (questionId: string) => Promise<void>;
  onAnswerSaved: () => void;
  isUpdatingAnswer?: boolean;
  onModalStateChange?: (isOpen: boolean) => void;
}

export const DisputeCard: React.FC<DisputeCardProps> = ({
  dispute,
  currentUserId,
  onAgreeWithPartner,
  isUpdatingAnswer = false,
  onAnswerSaved,
  onModalStateChange,
}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isChangeAnswerModalOpen, setIsChangeAnswerModalOpen] = useState(false);

  // Helper functions to notify parent of modal state changes
  const openConfirmationModal = () => {
    setIsConfirmationModalOpen(true);
    onModalStateChange?.(true);
  };

  const closeConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    onModalStateChange?.(false);
  };

  const openChangeAnswerModal = () => {
    setIsChangeAnswerModalOpen(true);
    onModalStateChange?.(true);
  };

  const closeChangeAnswerModal = () => {
    setIsChangeAnswerModalOpen(false);
    onModalStateChange?.(false);
  };
  const [isSavingPartnerAnswer, setIsSavingPartnerAnswer] = useState(false);

  const [upsertAnswer] = useUpsertAnswerMutation();

  const [userAnswer, partnerAnswer] =
    dispute.userId === currentUserId
      ? [dispute.answerA, dispute.answerB]
      : [dispute.answerB, dispute.answerA];

  // Helper to check if the disputed question is a parent question
  const hasChildQuestions = () => {
    return dispute.question.parentConditionalVisibilities.some(
      (cv) => cv.parentQuestionId === dispute.question.id,
    );
  };

  return (
    <Card className="dispute-card">
      <CardHeader
        isResolved={false}
        title={dispute.question.parentQuestionGroup.title}
        groupIcon={dispute.question.parentQuestionGroup.groupIcon}
      />

      <QuestionSection>
        <ParagraphHeaderLarge>{dispute.question.title}</ParagraphHeaderLarge>
        <AnswersContainer>
          <ReadOnlyAnswerDisplay
            label="Your Answer"
            answer={userAnswer}
            questionOptions={dispute.question.questionOptions.map((option) => ({
              ...option,
              deletedAt: option.deletedAt ? new Date(option.deletedAt) : null,
            }))}
            inputType={dispute.question.inputType}
          />
          <ReadOnlyAnswerDisplay
            label="Your Partner's Answer"
            answer={partnerAnswer}
            questionOptions={dispute.question.questionOptions.map((option) => ({
              ...option,
              deletedAt: option.deletedAt ? new Date(option.deletedAt) : null,
            }))}
            inputType={dispute.question.inputType}
          />
        </AnswersContainer>
      </QuestionSection>

      <CardFooter>
        <SecondaryButton
          onClick={() => {
            openChangeAnswerModal();
          }}
        >
          Change my Answer
        </SecondaryButton>
        <PrimaryButton
          onClick={async () => {
            // If the disputed question is a parent question, save the partner's answer first, then open the questionnaire modal
            if (hasChildQuestions()) {
              setIsSavingPartnerAnswer(true);
              try {
                // Save partner's answer as user's answer
                await upsertAnswer({
                  questionId: dispute.question.id,
                  userId: currentUserId,
                  documentId: dispute.documentId,
                  rootQuestionId:
                    dispute.question.rootQuestionId ?? dispute.question.id,
                  sectionId: dispute.question.parentQuestionnaireSectionId,
                  ...(partnerAnswer.valueTyped && {
                    valueTyped: partnerAnswer.valueTyped,
                  }),
                  questionOptionIds: partnerAnswer.answerQuestionOptions.map(
                    (aqo) => aqo.questionOptionId,
                  ),
                }).unwrap();

                openChangeAnswerModal();
              } catch (error) {
                console.error("Failed to save partner's answer:", error);
              } finally {
                setIsSavingPartnerAnswer(false);
              }
            } else {
              openConfirmationModal();
            }
          }}
          disabled={isUpdatingAnswer || isSavingPartnerAnswer}
        >
          {isSavingPartnerAnswer ? "Saving..." : "Agree with my partner"}
        </PrimaryButton>
      </CardFooter>

      {isConfirmationModalOpen && (
        <AcceptDisputeModal
          onClose={() => {
            closeConfirmationModal();
          }}
          onConfirm={() => {
            onAgreeWithPartner(dispute.question.id)
              .then(() => {
                // Invalidate disputes cache to show updated disputes list after change
                onAnswerSaved();
                closeConfirmationModal();
              })
              .catch((error: unknown) => {
                console.error("Failed to agree with partner:", error);
              });
          }}
          title="Confirm Answer Change"
          questionTitle={dispute.question.title}
          answerToShow={partnerAnswer}
          questionOptions={dispute.question.questionOptions.map((option) => ({
            ...option,
            deletedAt: option.deletedAt ? new Date(option.deletedAt) : null,
          }))}
          confirmText="Confirm"
          cancelText="Cancel"
          isLoading={isUpdatingAnswer}
          inputType={dispute.question.inputType}
        />
      )}

      {isChangeAnswerModalOpen && (
        <ChangeAnswerModal
          onClose={() => {
            // Invalidate disputes cache for potential answer changes
            onAnswerSaved();
            closeChangeAnswerModal();
          }}
          dispute={dispute}
          title="Change Your Answer"
        />
      )}
    </Card>
  );
};
